import React, { useState, useRef } from 'react';
import { Plus, X, Upload, Loader, Edit, Trash2, ChevronUp, ChevronDown, Settings } from 'lucide-react';
import { Course, Module } from '../types';
import PreUploadScormEditor from './PreUploadScormEditor';

interface ModuleFile {
  file: File;
  title: string;
  progress: number;
}

// Unified module interface for display
interface UnifiedModule {
  id: string; // For new modules, this will be a temp ID
  moduleId?: number; // Only for existing modules
  title: string;
  type: 'existing' | 'new';
  order: number;
  file_path?: string;
  size?: number;
  fileData?: File; // Only for new modules
  is_scorm?: boolean; // For existing modules
  wasPreEdited?: boolean; // Track if this module was pre-edited
}

interface EditCourseFormProps {
  course: Course;
  onSubmit: (courseId: number, formData: FormData) => Promise<void>;
  onCancel: () => void;
  onEditScorm?: (moduleId: number, moduleName: string) => void;
}

const EditCourseForm: React.FC<EditCourseFormProps> = ({ course, onSubmit, onCancel, onEditScorm }) => {
  const [courseTitle, setCourseTitle] = useState(course.title);
  const [isAssignedToAll, setIsAssignedToAll] = useState(course.is_assigned_to_all || false);
  
  // Initialize unified modules list
  const [unifiedModules, setUnifiedModules] = useState<UnifiedModule[]>(() => {
    const existingMods = (course.modules || [])
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(module => ({
        id: `existing-${module.id}`,
        moduleId: module.id,
        title: module.title,
        type: 'existing' as const,
        order: module.order || 0,
        file_path: module.file_path,
        size: module.size,
        is_scorm: module.is_scorm
      }));
    return existingMods;
  });
  
  const [modulesToDelete, setModulesToDelete] = useState<number[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Pre-upload SCORM editing state
  const [showScormEditor, setShowScormEditor] = useState(false);
  const [selectedScormFile, setSelectedScormFile] = useState<File | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'video/mp4',
    'video/webm',
    'audio/mpeg',
    'audio/wav',
    'application/zip',
    'application/x-zip-compressed'
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newModules = Array.from(files).map((file, index) => {
      if (!allowedTypes.includes(file.type)) {
        setError(`File type not supported: ${file.name}`);
        return null;
      }
      return {
        id: `new-${Date.now()}-${index}`,
        title: file.name.split('.')[0],
        type: 'new' as const,
        order: unifiedModules.length + index + 1,
        fileData: file,
        size: file.size,
        is_scorm: file.type === 'application/zip' || file.name.endsWith('.zip')
      } as UnifiedModule;
    }).filter((module): module is UnifiedModule => module !== null);

    setUnifiedModules(prev => [...prev, ...newModules]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveModule = (moduleId: string) => {
    const module = unifiedModules.find(m => m.id === moduleId);
    if (module?.type === 'existing' && module.moduleId) {
      if (window.confirm('Are you sure you want to delete this module?')) {
        setModulesToDelete(prev => [...prev, module.moduleId!]);
        setUnifiedModules(prev => prev.filter(m => m.id !== moduleId));
        // Recalculate orders
        updateModuleOrders();
      }
    } else {
      setUnifiedModules(prev => prev.filter(m => m.id !== moduleId));
      // Recalculate orders
      updateModuleOrders();
    }
  };

  // Handle pre-upload SCORM editing
  const handleEditScorm = (moduleId: string) => {
    const module = unifiedModules.find(m => m.id === moduleId);
    if (module?.type === 'new' && module.fileData) {
      setSelectedScormFile(module.fileData);
      setSelectedModuleId(moduleId);
      setShowScormEditor(true);
    }
  };

  const handleScormEditSave = (editedFile: File) => {
    if (selectedModuleId) {
      setUnifiedModules(prev => prev.map(module => 
        module.id === selectedModuleId 
          ? { ...module, fileData: editedFile, size: editedFile.size, wasPreEdited: true }
          : module
      ));
    }
    setShowScormEditor(false);
    setSelectedScormFile(null);
    setSelectedModuleId(null);
  };

  const handleScormEditCancel = () => {
    setShowScormEditor(false);
    setSelectedScormFile(null);
    setSelectedModuleId(null);
  };

  const handleModuleTitleChange = (moduleId: string, newTitle: string) => {
    setUnifiedModules(prev => prev.map(module => 
      module.id === moduleId ? { ...module, title: newTitle } : module
    ));
  };

  const updateModuleOrders = () => {
    setUnifiedModules(prev => prev.map((module, index) => ({
      ...module,
      order: index + 1
    })));
  };

  const handleMoveModuleUp = (moduleId: string) => {
    setUnifiedModules(prev => {
      const modules = [...prev];
      const currentIndex = modules.findIndex(m => m.id === moduleId);
      
      if (currentIndex > 0) {
        // Swap with previous module
        [modules[currentIndex], modules[currentIndex - 1]] = [modules[currentIndex - 1], modules[currentIndex]];
        
        // Update order values
        modules.forEach((module, index) => {
          module.order = index + 1;
        });
      }
      
      return modules;
    });
  };

  const handleMoveModuleDown = (moduleId: string) => {
    setUnifiedModules(prev => {
      const modules = [...prev];
      const currentIndex = modules.findIndex(m => m.id === moduleId);
      
      if (currentIndex < modules.length - 1) {
        // Swap with next module
        [modules[currentIndex], modules[currentIndex + 1]] = [modules[currentIndex + 1], modules[currentIndex]];
        
        // Update order values
        modules.forEach((module, index) => {
          module.order = index + 1;
        });
      }
      
      return modules;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!courseTitle.trim()) {
      setError('Course title is required');
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('title', courseTitle);
      formData.append('is_assigned_to_all', isAssignedToAll.toString());

      // Separate existing and new modules
      const existingModules = unifiedModules.filter(m => m.type === 'existing');
      const newModules = unifiedModules.filter(m => m.type === 'new');

      // Add existing modules data (for title and order updates)
      if (existingModules.length > 0) {
        const existingModulesData = existingModules.map(module => ({
          id: module.moduleId!,
          title: module.title,
          order: module.order
        }));
        formData.append('existingModules', JSON.stringify(existingModulesData));
      }

      // Add modules to delete
      if (modulesToDelete.length > 0) {
        formData.append('modulesToDelete', JSON.stringify(modulesToDelete));
      }

      // Add new module data with correct order positions
      if (newModules.length > 0) {
        const newModuleData = newModules.map(module => ({
          title: module.title,
          order: module.order
        }));
        formData.append('newModuleData', JSON.stringify(newModuleData));

        // Add new files in the same order
        newModules.forEach(module => {
          if (module.fileData) {
            formData.append('newModuleFiles', module.fileData);
          }
        });
      }

      await onSubmit(course.id, formData);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update course');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg mb-8">
      <h2 className="text-2xl font-semibold mb-4">Edit Course</h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-100">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="title" className="block text-sm font-medium mb-1">
            Course Title
          </label>
          <input
            type="text"
            id="title"
            value={courseTitle}
            onChange={(e) => setCourseTitle(e.target.value)}
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
            required
            disabled={isUploading}
          />
        </div>

        <div className="mb-4 p-4 bg-gray-700 rounded-lg">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_assigned_to_all"
              checked={isAssignedToAll}
              onChange={(e) => setIsAssignedToAll(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 bg-gray-600 border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
              disabled={isUploading}
            />
            <label htmlFor="is_assigned_to_all" className="text-sm font-medium">
              Assign to all users automatically
            </label>
          </div>
        </div>

        {/* Unified Modules List */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Course Modules</label>
          
          {unifiedModules.map((module, index) => (
            <div key={module.id} className="mb-3 p-3 bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 flex-1">
                  {/* Move up/down buttons */}
                  <div className="flex flex-col space-y-1">
                    <button
                      type="button"
                      onClick={() => handleMoveModuleUp(module.id)}
                      className={`p-1 rounded ${
                        index === 0 
                          ? 'text-gray-500 cursor-not-allowed' 
                          : 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/20'
                      }`}
                      disabled={isUploading || index === 0}
                      title="Move Up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveModuleDown(module.id)}
                      className={`p-1 rounded ${
                        index === unifiedModules.length - 1 
                          ? 'text-gray-500 cursor-not-allowed' 
                          : 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/20'
                      }`}
                      disabled={isUploading || index === unifiedModules.length - 1}
                      title="Move Down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Order indicator and type badge */}
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-400 font-mono min-w-[2rem]">
                      {index + 1}.
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      module.type === 'new' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-blue-600 text-white'
                    }`}>
                      {module.type === 'new' ? 'NEW' : 'EXISTING'}
                    </span>
                  </div>
                  
                  <input
                    type="text"
                    value={module.title}
                    onChange={(e) => handleModuleTitleChange(module.id, e.target.value)}
                    className="flex-1 p-2 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:border-blue-500"
                    placeholder="Module title"
                    required
                    disabled={isUploading}
                  />
                </div>
                
                <div className="flex items-center space-x-1">
                  {/* SCORM Edit Button - for both existing and new SCORM modules */}
                  {module.is_scorm && (
                    module.type === 'existing' && onEditScorm && !module.wasPreEdited ? (
                      <button
                        type="button"
                        onClick={() => onEditScorm(module.moduleId!, module.title)}
                        className="text-orange-400 hover:text-orange-300 p-1"
                        disabled={isUploading}
                        title="Edit SCORM Timing"
                      >
                        <Settings className="h-5 w-5" />
                      </button>
                    ) : module.type === 'new' ? (
                      <button
                        type="button"
                        onClick={() => handleEditScorm(module.id)}
                        className="text-blue-400 hover:text-blue-300 p-1"
                        disabled={isUploading}
                        title="Edit SCORM Before Upload"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                    ) : module.wasPreEdited ? (
                      <span className="text-green-400 text-xs px-2 py-1 bg-green-900/20 rounded" title="Pre-edited SCORM content">
                        ✓ Pre-edited
                      </span>
                    ) : null
                  )}
                  
                  {/* Delete/Remove Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveModule(module.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                    disabled={isUploading}
                    title={module.type === 'existing' ? 'Delete Module' : 'Remove Module'}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-400 ml-16">
                {module.type === 'existing' ? (
                  <>
                    Existing file: {module.file_path?.split('/').pop() || 'Unknown file'}
                    {module.size && ` (${(module.size / 1024 / 1024).toFixed(2)} MB)`}
                  </>
                ) : (
                  <>
                    New file: {module.fileData?.name} ({(module.size! / 1024 / 1024).toFixed(2)} MB)
                  </>
                )}
              </div>
            </div>
          ))}

          {unifiedModules.length === 0 && (
            <div className="text-gray-400 text-sm">No modules</div>
          )}
        </div>

        {/* Add New Modules */}
        <div className="mb-4">
          <div className="mt-3">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              multiple
              accept=".pdf,.pptx,.mp4,.webm,.mp3,.wav,.zip"
              disabled={isUploading}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 transition duration-300 flex items-center"
              disabled={isUploading}
            >
              <Upload className="mr-2" /> Add Module Files
            </button>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors flex items-center"
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Loader className="animate-spin -ml-1 mr-2 h-5 w-5" />
                Updating...
              </>
            ) : (
              <>
                <Edit className="mr-2" />
                Update Course
              </>
            )}
          </button>
        </div>
      </form>
      
      {/* Pre-upload SCORM Editor Modal */}
      {showScormEditor && selectedScormFile && (
        <PreUploadScormEditor
          file={selectedScormFile}
          onSave={handleScormEditSave}
          onCancel={handleScormEditCancel}
        />
      )}
    </div>
  );
};

export default EditCourseForm; 